import Backdrop from '@/components/ui/backdrop';
import { Location } from '@/core/entities';
import LocationsEmpty from '@/features/locations/LocationsEmpty';
import RootLocationManagement from '@/features/locations/locations-management/RootLocationsManagement';
import { useLevelLocations } from '@/queries/locations';
import { useSite } from '@/queries/sites';
import useAuth from '@/shared/context/auth-context';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Paper } from '@mui/material';
import { Box } from '@mui/system';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LocationsPage: FunctionComponent = (): ReactElement => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const siteId = useMemo(() => user && user.sites?.[0]?.id, [user]);
	const { query: siteQuery } = useSite(siteId);
	const { data: site, isLoading: isSiteLoading, isFetched: isSiteFetched } = siteQuery;

	const rootLevel = useMemo(() => site?.levels.find((level) => level.parent?.id === null), [site]);

	const showEmptyState = useMemo(
		() => !siteId || ((!site || !rootLevel) && isSiteFetched),
		[siteId, site, rootLevel, isSiteFetched]
	);

	const [locations, setLocations] = useState<Location[]>([]);

	const {
		query: { isLoading: isLevelLocationsLoading, data: levelLocationsPages },
	} = useLevelLocations({
		level: rootLevel,
		queryParams: { limit: 99999 },
	});

	useEffect(() => {
		const { locations } = levelLocationsPages?.pages[0] || {};

		if (locations) {
			setLocations(locations.data);
		}
	}, [levelLocationsPages]);

	if (showEmptyState) return <LocationsEmpty />;
	if (isSiteLoading) return <Backdrop />;

	return (
		<AdminPanelPage title={site!.name} breadcrumbs={[{ title: site!.name }]}>
			<Box component={Paper} padding={3}>
				<RootLocationManagement
					site={site!}
					level={rootLevel!}
					header={{}}
					table={{
						data: locations,
						isLoading: isLevelLocationsLoading,
						rowAction: (location) => navigate(`/locations/${location.id}`),
					}}
				/>
			</Box>
		</AdminPanelPage>
	);
};

export default LocationsPage;
